<template>
  <div class="vf-wrap" style="width:100%;height:100%;">
    <canvas ref="cvs" class="vf-canvas"/>
  </div>
</template>

<script>
const MOOD_COLOR = {
  neutral: [41,  184, 212],
  info:    [41,  184, 212],
  happy:   [232, 160,  32],
  success: [34,  201, 122],
  error:   [217,  68,  85],
  angry:   [192,  32,  48],
}

const EXPR = {
  neutral:   { browLA:0,     browRA:0,     browLY:0,  browRY:0,  eyeLS:1,    eyeRS:1,    squint:0,    mCurve:0.5,  mWidth:1,   tilt:0  },
  relax:     { browLA:0,     browRA:0,     browLY:1,  browRY:1,  eyeLS:0.82, eyeRS:0.82, squint:0.06, mCurve:0.35, mWidth:0.9, tilt:0  },
  happy:     { browLA:-0.16, browRA:0.16,  browLY:-4, browRY:-4, eyeLS:1,    eyeRS:1,    squint:0.1,  mCurve:1,    mWidth:1.1, tilt:2  },
  excited:   { browLA:-0.22, browRA:0.22,  browLY:-7, browRY:-7, eyeLS:1.15, eyeRS:1.15, squint:0,    mCurve:1.2,  mWidth:1.3, tilt:5  },
  bigsmile:  { browLA:-0.18, browRA:0.18,  browLY:-5, browRY:-5, eyeLS:0.7,  eyeRS:0.7,  squint:0.18, mCurve:1.4,  mWidth:1.2, tilt:3  },
  winkL:     { browLA:-0.2,  browRA:0.1,   browLY:-5, browRY:0,  eyeLS:0.08, eyeRS:1,    squint:0,    mCurve:0.9,  mWidth:1,   tilt:2  },
  winkR:     { browLA:0.1,   browRA:-0.2,  browLY:0,  browRY:-5, eyeLS:1,    eyeRS:0.08, squint:0,    mCurve:0.9,  mWidth:1,   tilt:-2 },
  surprised: { browLA:-0.1,  browRA:0.1,   browLY:-8, browRY:-8, eyeLS:1.25, eyeRS:1.25, squint:0,    mCurve:0.1,  mWidth:0.7, tilt:0  },
  sleepy:    { browLA:0.05,  browRA:-0.05, browLY:2,  browRY:2,  eyeLS:0.38, eyeRS:0.38, squint:0.12, mCurve:0.2,  mWidth:0.8, tilt:-1 },
  concerned: { browLA:0.2,   browRA:-0.2,  browLY:-2, browRY:-2, eyeLS:0.88, eyeRS:0.88, squint:0,    mCurve:0.15, mWidth:0.85,tilt:-2 },
  frown:     { browLA:0.3,   browRA:-0.3,  browLY:-5, browRY:-5, eyeLS:0.78, eyeRS:0.78, squint:0.1,  mCurve:-0.2, mWidth:0.8, tilt:-4 },
}

const MOOD_EXPRS = {
  neutral: ['neutral','relax','sleepy','neutral','winkR','neutral','happy','neutral','relax'],
  info:    ['neutral','relax','winkL','neutral','surprised','neutral'],
  happy:   ['happy','bigsmile','excited','happy','winkL','bigsmile'],
  success: ['happy','excited','bigsmile','happy','winkR'],
  error:   ['concerned','frown','concerned'],
  angry:   ['frown','concerned','frown'],
}

function lerp(a,b,t){ return a+(b-a)*t }
function ease(t){ return t<.5?2*t*t:-1+(4-2*t)*t }
function lerpExpr(a,b,t){
  const r={}, e=ease(Math.min(1,t))
  for(const k of Object.keys(a)) r[k]=lerp(a[k],b[k],e)
  return r
}

export default {
  name: 'VikiFace',
  props: {
    mood:     { type:String,  default:'neutral' },
    speaking: { type:Boolean, default:false },
    size:     { type:Number,  default:34 },
  },

  mounted(){
    // Size canvas to actual element dimensions (retina x2)
    const el = this.$refs.cvs
    const pr = window.devicePixelRatio || 1
    const resize = () => {
      const w = this.$el.clientWidth
      const h = this.$el.clientHeight
      el.width  = w * pr
      el.height = h * pr
    }
    resize()
    this.__ro = new ResizeObserver(resize)
    this.__ro.observe(this.$el)

    const initCol = MOOD_COLOR[this.mood] || MOOD_COLOR.neutral
    this.__s = {
      t:0, last:null,
      blinkT:0, nextBlink:3+Math.random()*3, blinkAmt:1,
      phaseTime:0, phaseDur:1.8+Math.random()*1.5, curPhase:0, nextPhase:1,
      colR: initCol[0], colG: initCol[1], colB: initCol[2],
    }
    this.__raf = requestAnimationFrame(ts => this.__loop(ts))
  },

  beforeDestroy(){
    cancelAnimationFrame(this.__raf)
    if(this.__ro) this.__ro.disconnect()
  },

  methods:{
    __loop(ts){
      const s  = this.__s
      const dt = Math.min(s.last!=null?(ts-s.last)/1000:0, 0.05)
      s.last   = ts
      s.t     += dt

      s.phaseTime += dt/s.phaseDur
      if(s.phaseTime>=1){
        s.phaseTime = 0
        s.curPhase  = s.nextPhase
        const list  = MOOD_EXPRS[this.mood]||MOOD_EXPRS.neutral
        s.nextPhase = (s.nextPhase+1)%list.length
        s.phaseDur  = (this.speaking?0.8:1.8)+Math.random()*1.2
      }

      s.blinkT += dt
      if(s.blinkT>=s.nextBlink){ s.blinkT=0; s.nextBlink=3.5+Math.random()*3 }
      if      (s.blinkT<0.08)  s.blinkAmt = Math.max(0, 1-s.blinkT/0.04)
      else if (s.blinkT<0.18)  s.blinkAmt = Math.min(1,(s.blinkT-0.08)/0.1)
      else                      s.blinkAmt = 1

      // Lerp background color toward target mood color
      const tgt = MOOD_COLOR[this.mood] || MOOD_COLOR.neutral
      const sp = 0.012
      s.colR = lerp(s.colR, tgt[0], sp)
      s.colG = lerp(s.colG, tgt[1], sp)
      s.colB = lerp(s.colB, tgt[2], sp)

      this.__draw(s)
      this.__raf = requestAnimationFrame(ts2=>this.__loop(ts2))
    },

    __draw(s){
      const cvs = this.$refs.cvs
      if(!cvs) return
      const ctx = cvs.getContext('2d')
      const W   = cvs.width
      const H   = cvs.height
      const t   = s.t
      // Scale based on height — 40% larger total
      const sc  = H / 138

      ctx.clearRect(0,0,W,H)

      // ── Smooth background color (lerped each frame) ──────────
      ctx.fillStyle = `rgb(${s.colR|0},${s.colG|0},${s.colB|0})`
      ctx.fillRect(0, 0, W, H)

      // White features
      const C = '255,255,255'

      // ── Expression ──────────────────────────────────────────
      const list  = MOOD_EXPRS[this.mood]||MOOD_EXPRS.neutral
      const exprA = EXPR[list[s.curPhase]]  || EXPR.neutral
      const exprB = EXPR[list[s.nextPhase]] || EXPR.neutral
      const expr  = lerpExpr(exprA, exprB, s.phaseTime)

      // Organic wander
      const wanderFrac = Math.sin(t*0.31)*0.6 + Math.sin(t*0.17)*0.4  // -1..1
      const wanderX    = wanderFrac * W * 0.12
      const wanderY    = (Math.sin(t*0.23)*0.6 + Math.sin(t*0.11)*0.4) * H * 0.18
      const bob        = expr.mCurve * 1.5 * sc
      const faceCX     = W/2 + wanderX
      const faceCY     = H/2 + wanderY + bob

      // 3D head turn toward center — squish X by cos(angle), max 45°
      // When face is right (+wander), it turns left → negative horizontal scale stays 1 in center
      const cosA = Math.cos(wanderFrac * Math.PI * 0.25)

      ctx.save()
      ctx.translate(faceCX, faceCY)
      ctx.rotate(expr.tilt * 0.014)
      ctx.scale(cosA, 1)            // perspective compression of all horizontal features
      ctx.translate(-faceCX, -faceCY)

      // ── COWBOY HAT ───────────────────────────────────────────
      // Cowboy hat = SHORT wide crown + center crease + dramatically wide brim
      const hatBrimY  = faceCY - 44 * sc  // brim sits just above eyebrows
      const crownH    = 14 * sc           // SHORT — cowboy hat is flat, not tall
      const crownW    = 26 * sc           // wide
      const brimHalfW = 50 * sc           // brim is 2× the crown width
      const brimThick = 5  * sc

      // Crown — cowboy silhouette: two bumps with center dent at top
      // Drawn as a custom path so the top has the characteristic crease
      ctx.beginPath()
      ctx.moveTo(faceCX - crownW,       hatBrimY)                               // BL
      ctx.lineTo(faceCX - crownW,       hatBrimY - crownH * 0.6)               // left side up
      // Left bump
      ctx.quadraticCurveTo(
        faceCX - crownW * 0.55,         hatBrimY - crownH * 1.18,
        faceCX - crownW * 0.12,         hatBrimY - crownH * 0.82
      )
      // Center dent (the crease)
      ctx.quadraticCurveTo(
        faceCX,                          hatBrimY - crownH * 0.52,
        faceCX + crownW * 0.12,         hatBrimY - crownH * 0.82
      )
      // Right bump
      ctx.quadraticCurveTo(
        faceCX + crownW * 0.55,         hatBrimY - crownH * 1.18,
        faceCX + crownW,               hatBrimY - crownH * 0.6
      )
      ctx.lineTo(faceCX + crownW,       hatBrimY)                               // BR
      ctx.closePath()
      ctx.fillStyle = '#6B2D0A'
      ctx.fill()

      // Crown right-side shading (3D depth)
      ctx.save()
      ctx.globalAlpha = 0.25
      ctx.beginPath()
      ctx.moveTo(faceCX + crownW * 0.45, hatBrimY)
      ctx.lineTo(faceCX + crownW,        hatBrimY - crownH * 0.6)
      ctx.quadraticCurveTo(faceCX + crownW * 0.55, hatBrimY - crownH * 1.18, faceCX + crownW * 0.12, hatBrimY - crownH * 0.82)
      ctx.quadraticCurveTo(faceCX, hatBrimY - crownH * 0.52, faceCX + crownW * 0.45, hatBrimY)
      ctx.fillStyle = '#000'
      ctx.fill()
      ctx.restore()

      // Center crease shadow (the dent)
      ctx.save()
      ctx.globalAlpha = 0.3
      ctx.beginPath()
      ctx.ellipse(faceCX, hatBrimY - crownH * 0.65, crownW * 0.18, crownH * 0.2, 0, 0, Math.PI * 2)
      ctx.fillStyle = '#1a0000'
      ctx.fill()
      ctx.restore()

      // Hat band (dark strip at base of crown)
      ctx.beginPath()
      this.__rr(ctx, faceCX - crownW - sc, hatBrimY - 6 * sc, (crownW + sc) * 2, 5 * sc, sc)
      ctx.fillStyle = '#1a0800'
      ctx.fill()

      // Brim — wide, flat, slightly curved front/back
      // Draw as a bezier path so it dips at front center like a real cowboy hat
      ctx.save()
      const bd = brimThick * 0.5   // how much front dips
      ctx.beginPath()
      // Top of brim (relatively flat)
      ctx.moveTo(faceCX - brimHalfW, hatBrimY)
      ctx.bezierCurveTo(
        faceCX - brimHalfW * 0.5,  hatBrimY - bd * 0.5,
        faceCX + brimHalfW * 0.5,  hatBrimY - bd * 0.5,
        faceCX + brimHalfW,        hatBrimY
      )
      // Right tip down
      ctx.lineTo(faceCX + brimHalfW, hatBrimY + brimThick * 0.4)
      // Bottom of brim (dips in center = curls down at front)
      ctx.bezierCurveTo(
        faceCX + brimHalfW * 0.5,  hatBrimY + brimThick * 0.9,
        faceCX - brimHalfW * 0.5,  hatBrimY + brimThick * 0.9,
        faceCX - brimHalfW,        hatBrimY + brimThick * 0.4
      )
      ctx.closePath()
      ctx.fillStyle = '#7a3d18'
      ctx.fill()
      // Brim underside shadow
      ctx.globalAlpha = 0.25
      ctx.beginPath()
      ctx.ellipse(faceCX, hatBrimY + brimThick * 0.7, brimHalfW * 0.82, brimThick * 0.5, 0, 0, Math.PI * 2)
      ctx.fillStyle = '#000'
      ctx.fill()
      ctx.restore()

      // ── EYEBROWS ────────────────────────────────────────────
      const browGap = 28 * sc
      const browW   = 20 * sc
      const browH   = 4  * sc
      const browY   = faceCY - 40 * sc

      for(const side of [-1,1]){
        const bx = faceCX + browGap * side
        const by = browY + (side===-1 ? expr.browLY : expr.browRY) * sc
        const ba = side===-1 ? expr.browLA : expr.browRA
        ctx.save()
        ctx.translate(bx, by); ctx.rotate(ba)
        this.__rr(ctx, -browW/2, -browH/2, browW, browH, browH/2)
        ctx.fillStyle = `rgba(${C},0.85)`; ctx.fill()
        ctx.restore()
      }

      // ── EYES — vertical thin pills ──────────────────────────
      const eyeGap = 28 * sc
      const eyeW   = 10 * sc
      const eyeH   = 32 * sc
      const eyeY   = faceCY - 8 * sc

      for(const side of [-1,1]){
        const ex        = faceCX + eyeGap * side
        const eyeOpenSc = (side===-1 ? expr.eyeLS : expr.eyeRS) * (1 - expr.squint * 0.5)
        const openH     = eyeH * s.blinkAmt * eyeOpenSc

        if(openH < 2*sc){
          this.__rr(ctx, ex-eyeW/2, eyeY-sc, eyeW, 2*sc, sc)
          ctx.fillStyle = `rgba(${C},0.9)`; ctx.fill()
        } else {
          this.__rr(ctx, ex-eyeW/2, eyeY-openH/2, eyeW, openH, eyeW/2)
          ctx.fillStyle = `rgba(${C},0.95)`; ctx.fill()
        }
      }

      // ── NOSE — vertical pill, shifts with expression ──────────
      const noseW  = 6  * sc
      const noseH  = 12 * sc
      const noseShiftX = expr.tilt * 0.8 * sc   // drifts with tilt
      const noseShiftY = expr.mCurve * 2  * sc   // rises with smile
      const noseY  = faceCY + 16 * sc - noseShiftY
      this.__rr(ctx, faceCX - noseW/2 + noseShiftX, noseY - noseH/2, noseW, noseH, noseW/2)
      ctx.fillStyle = `rgba(${C},0.45)`; ctx.fill()

      // ── MOUTH — moves up/down with emotion ───────────────────
      const mouthPull = expr.mCurve * 3 * sc   // mouth rises when smiling
      const mouthY = faceCY + 36 * sc - mouthPull
      const mouthW = 26 * sc * expr.mWidth

      if(this.speaking){
        const w1 = Math.abs(Math.sin(t*7))
        const w2 = Math.abs(Math.sin(t*11)) * 0.35
        const wa = Math.min(1,(w1+w2)/1.2)
        const oh = 4*sc + wa*12*sc
        this.__rr(ctx, faceCX-mouthW/2, mouthY-oh/2, mouthW, oh, oh/2)
        ctx.fillStyle = `rgba(${C},${0.78+wa*0.15})`; ctx.fill()
      } else {
        const curve = (2 + expr.mCurve * 7) * sc
        ctx.beginPath()
        ctx.moveTo(faceCX-mouthW/2, mouthY)
        ctx.bezierCurveTo(
          faceCX-mouthW/2+mouthW*0.2, mouthY+curve,
          faceCX+mouthW/2-mouthW*0.2, mouthY+curve,
          faceCX+mouthW/2, mouthY
        )
        ctx.strokeStyle = `rgba(${C},0.88)`
        ctx.lineWidth = 3.5*sc; ctx.lineCap = 'round'; ctx.stroke()
      }

      ctx.restore()
    },

    __rr(ctx,x,y,w,h,r){
      const rr = Math.min(r, w/2, h/2)
      if(typeof ctx.roundRect === 'function'){
        ctx.beginPath(); ctx.roundRect(x,y,w,h,rr)
      } else {
        ctx.beginPath()
        ctx.moveTo(x+rr,y)
        ctx.lineTo(x+w-rr,y); ctx.arcTo(x+w,y,x+w,y+rr,rr)
        ctx.lineTo(x+w,y+h-rr); ctx.arcTo(x+w,y+h,x+w-rr,y+h,rr)
        ctx.lineTo(x+rr,y+h); ctx.arcTo(x,y+h,x,y+h-rr,rr)
        ctx.lineTo(x,y+rr); ctx.arcTo(x,y,x+rr,y,rr)
        ctx.closePath()
      }
    },
  },
}
</script>

<style scoped>
.vf-wrap { position: relative; overflow: hidden; cursor: pointer; }
.vf-canvas { width: 100%; height: 100%; display: block; }
</style>
